import { useEffect, useRef, useState, useCallback } from 'react';

// Custom event types for editor operations
export interface EditorUpdateEvent extends CustomEvent {
  detail: {
    content: string;
    previousContent?: string;
    articleId?: string;
  };
}

export interface HistoryEvent extends CustomEvent {
  detail: {
    type: 'add' | 'undo' | 'redo' | 'reset';
    content?: string;
    summary?: string;
    articleId?: string;
  };
}

export const EDITOR_EVENTS = {
  UPDATE_CONTENT: 'editor:updateContent',
  HISTORY_ACTION: 'editor:historyAction',
} as const;

// Hook for dispatching editor events
export function useEditorEventDispatcher() {
  const dispatchUpdateContent = (content: string, articleId?: string, previousContent?: string) => {
    const event = new CustomEvent(EDITOR_EVENTS.UPDATE_CONTENT, {
      detail: { content, articleId, previousContent }
    }) as EditorUpdateEvent;

    window.dispatchEvent(event);
  };

  const dispatchHistoryAction = (type: 'add' | 'undo' | 'redo' | 'reset', content?: string, summary?: string, articleId?: string) => {
    const event = new CustomEvent(EDITOR_EVENTS.HISTORY_ACTION, {
      detail: { type, content, summary, articleId }
    }) as HistoryEvent;

    window.dispatchEvent(event);
  };

  return {
    dispatchUpdateContent,
    dispatchHistoryAction,
  };
}

// Hook for listening to editor events
export function useEditorEventListener(
  articleId: string | undefined,
  onUpdateContent: (content: string, previousContent?: string) => void
) {
  const handlerRef = useRef<(event: Event) => void>();

  useEffect(() => {
    const handler = (event: Event) => {
      const editorEvent = event as EditorUpdateEvent;
      const { content, previousContent, articleId: eventArticleId } = editorEvent.detail;

      // Only handle events for this specific editor instance
      if (!eventArticleId || eventArticleId === articleId) {
        onUpdateContent(content, previousContent);
      }
    };

    handlerRef.current = handler;
    window.addEventListener(EDITOR_EVENTS.UPDATE_CONTENT, handler);

    return () => {
      if (handlerRef.current) {
        window.removeEventListener(EDITOR_EVENTS.UPDATE_CONTENT, handlerRef.current);
      }
    };
  }, [articleId, onUpdateContent]);
}

// Hook for listening to history events
export function useHistoryEventListener(
  articleId: string | undefined,
  onHistoryAction: (type: 'add' | 'undo' | 'redo' | 'reset', content?: string, summary?: string) => void
) {
  const handlerRef = useRef<(event: Event) => void>();

  useEffect(() => {
    const handler = (event: Event) => {
      const historyEvent = event as HistoryEvent;
      const { type, content, summary, articleId: eventArticleId } = historyEvent.detail;

      // Only handle events for this specific editor instance
      if (!eventArticleId || eventArticleId === articleId) {
        onHistoryAction(type, content, summary);
      }
    };

    handlerRef.current = handler;
    window.addEventListener(EDITOR_EVENTS.HISTORY_ACTION, handler);

    return () => {
      if (handlerRef.current) {
        window.removeEventListener(EDITOR_EVENTS.HISTORY_ACTION, handlerRef.current);
      }
    };
  }, [articleId, onHistoryAction]);
}

// Hook for managing history state with event integration
export interface HistorySnapshot {
  content: string;
  title: string;
}

export function useHistoryManager(initialContent: string, initialTitle: string = '', articleId?: string) {
  const [history, setHistory] = useState<HistorySnapshot[]>([{ content: initialContent, title: initialTitle }]);
  const [historyPointer, setHistoryPointer] = useState(0);
  const [changeSummaries, setChangeSummaries] = useState<string[]>(['initial content']);

  // Update initial history when initialTitle changes (e.g. after data fetch)
  useEffect(() => {
    if (initialTitle && history.length === 1 && history[0].title === '') {
      setHistory(prev => [{ ...prev[0], title: initialTitle }]);
    }
  }, [initialTitle]);

  const { dispatchHistoryAction } = useEditorEventDispatcher();

  const updateHistory = useCallback((newContent: string, newTitle: string, summary: string = 'manual edit') => {
    const newHistory = history.slice(0, historyPointer + 1);
    const newSummaries = changeSummaries.slice(0, historyPointer + 1);

    // If title is empty/missing, fall back to previous history entry's title
    // This handles race conditions where title hasn't loaded yet
    const previousTitle = newHistory.length > 0 ? newHistory[newHistory.length - 1].title : '';
    if (!newTitle) {
      console.warn('Edit set no title, use previous title ' + previousTitle, summary)
      newHistory.push({ content: newContent, title: previousTitle });
    }
    else {
      if (previousTitle !== newTitle) {
        console.log('Edit change title to ' + newTitle, summary)
      }
      newHistory.push({ content: newContent, title: newTitle });
    }

    newSummaries.push(summary);
    setHistory(newHistory);
    setChangeSummaries(newSummaries);
    setHistoryPointer(newHistory.length - 1);
  }, [history, historyPointer, changeSummaries]);

  const undo = useCallback(() => {
    if (historyPointer > 0) {
      const newPointer = historyPointer - 1;
      const undoSnapshot = history[newPointer];
      setHistoryPointer(newPointer);
      dispatchHistoryAction('undo', undoSnapshot.content, changeSummaries[historyPointer], articleId);
      return undoSnapshot;
    }
    return null;
  }, [historyPointer, history, changeSummaries, dispatchHistoryAction, articleId]);

  const redo = useCallback(() => {
    if (historyPointer < history.length - 1) {
      const newPointer = historyPointer + 1;
      const redoSnapshot = history[newPointer];
      setHistoryPointer(newPointer);
      dispatchHistoryAction('redo', redoSnapshot.content, changeSummaries[newPointer], articleId);
      return redoSnapshot;
    }
    return null;
  }, [historyPointer, history, changeSummaries, dispatchHistoryAction, articleId]);

  const resetHistory = useCallback((content: string, title: string) => {
    setHistory([{ content, title }]);
    setHistoryPointer(0);
    setChangeSummaries(['initial content']);
    dispatchHistoryAction('reset', content, 'reset to initial', articleId);
  }, [dispatchHistoryAction, articleId]);

  const canUndo = historyPointer > 0;
  const canRedo = historyPointer < history.length - 1;
  const lastChangeSummary = historyPointer > 0 ? changeSummaries[historyPointer] : '';
  const nextChangeSummary = historyPointer < history.length - 1 ? changeSummaries[historyPointer + 1] : '';

  return {
    history,
    historyPointer,
    changeSummaries,
    updateHistory,
    undo,
    redo,
    resetHistory,
    canUndo,
    canRedo,
    lastChangeSummary,
    nextChangeSummary,
  };
}
